# Clinical Data APIs Overview

FHIRfly provides instant access to healthcare reference data through simple REST APIs.

## Available APIs

| API | Description | Data Source |
|-----|-------------|-------------|
| [NDC](/docs/value-apis/ndc) | National Drug Codes | FDA NDC Directory |
| [NPI](/docs/value-apis/npi) | Provider Identifiers | CMS NPPES |
| [RxNorm](/docs/value-apis/rxnorm) | Drug Terminology | NLM RxNorm |
| [RxClass](/docs/value-apis/rxclass) | Drug Classifications | NLM MED-RT |
| [UCUM](/docs/value-apis/ucum) | Units of Measure | Regenstrief UCUM |
| [LOINC](/docs/value-apis/loinc) | Lab Codes | Regenstrief LOINC |
| [ICD-10](/docs/value-apis/icd10) | Diagnosis Codes | CMS ICD-10 |
| [CVX](/docs/value-apis/cvx) | Vaccine Codes | CDC CVX |
| [MVX](/docs/value-apis/mvx) | Manufacturer Codes | CDC MVX |
| [FDA Labels](/docs/value-apis/fda-labels) | Drug Labels | FDA DailyMed |
| [SNOMED CT](/docs/value-apis/snomed) | Clinical Concepts | SNOMED IPS Free Set |
| [HCPCS Level II](/docs/value-apis/hcpcs) | Procedure & Supply Codes | CMS HCPCS |
| [MS-DRG](/docs/value-apis/msdrg) | Diagnosis Related Groups | CMS IPPS |
| [Place of Service](/docs/value-apis/pos) | Place of Service Codes | CMS POS |
| [J-Code/NDC Crosswalk](/docs/value-apis/jcode) | J-Code to NDC Mapping | CMS ASP NDC-HCPCS |

## Common Patterns

All Clinical Data APIs follow consistent patterns:

### Single Lookup

```
GET /v1/{api}/{code}
```

### Batch Lookup

```
POST /v1/{api}/_batch
Content-Type: application/json
{"codes": ["code1", "code2", "code3"]}
```

### Response Shapes

```
GET /v1/{api}/{code}?shape=compact|standard|full
```

See [Response Shapes](/docs/response-shapes) for details on each format.

## Authentication

All Clinical Data API requests require authentication via API key or OAuth token.

<LanguageBlock>
  <Block language="node">
    ```typescript
    import { Fhirfly } from "@fhirfly-io/terminology";

    // API Key
    const client = new Fhirfly({ apiKey: "YOUR_API_KEY" });

    // Or OAuth2
    const client = new Fhirfly({
      clientId: "ffly_client_...",
      clientSecret: "ffly_secret_...",
    });

    const result = await client.ndc.lookup("0069-0151-01");
    ```
  </Block>
  <Block language="curl">
    ```bash
    # API Key
    curl -H "x-api-key: YOUR_API_KEY" \
      "https://api.fhirfly.io/v1/ndc/0069-0151-01"

    # OAuth Token
    curl -H "Authorization: Bearer TOKEN" \
      "https://api.fhirfly.io/v1/ndc/0069-0151-01"
    ```
  </Block>
</LanguageBlock>

## Response Format

All responses follow a consistent structure:

```json
{
  "data": { /* resource data */ },
  "meta": {
    "source": { /* provenance (full shape only) */ },
    "legal": { /* licensing info */ }
  }
}
```

## Error Responses

| Status | Description |
|--------|-------------|
| 400 | Invalid request (bad code format) |
| 401 | Missing or invalid authentication |
| 403 | Insufficient permissions (scope) |
| 404 | Code not found |
| 429 | Rate limit exceeded |
| 500 | Server error |

```json
{
  "error": "not_found",
  "error_description": "NDC code not found: 0000-0000-00"
}
```
